import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Toast from "src/components/Toast";
import constants from "./constants";
import { UserManager } from "oidc-client-ts";
import oidcConfig from "src/config/oidc/config";
import { logError } from "src/config/logLevel/logger";
import { getFirstDefined } from "src/utils/genericFunctions";
const authority: any = process.env.VITE_OIDC_CLIENT_AUTHORITY;
const clientId: any = process.env.VITE_OIDC_CLIENT_ID;

export const getMSToken = () => localStorage.getItem("ms_token");
export const getMSCookie = () => localStorage.getItem("ms_cookie");

type Headers = {
  "Content-Type": string;
  Authorization?: string;
  "x-request-id"?: string;
  "x-correlation-id"?: string;
  accept?: string;
  appRequestID?: string;
  ms_token?: string;
  ms_cookie?: string;
};

const DEFAULT_CONFIG: { headers: Headers; signal?: AbortSignal } = {
  headers: {
    "Content-Type": constants.DEFAULT_CONTENT_TYPE,
    accept: constants.DEFAULT_CONTENT_TYPE
  }
};

const MULTIPART_CONFIG: { headers: Headers } = {
  headers: {
    "Content-Type": constants.MULTIPART_CONTENT_TYPE
  }
};

const limitRecallToken = () => {
  let limit = constants.TOKEN_RECALL_LIMIT;
  setInterval(() => {
    limit = constants.TOKEN_RECALL_LIMIT;
  }, 300000);
  return () => (limit -= 1);
};

const getLimit = limitRecallToken();

export const requestMsToken = () => {
  const URL = constants.SERVICES_API_BASE_URL + "sp/v1/mstr/login";
  return get(URL);
};

let isRefreshingToken: boolean = false;
let refreshCallPromise: any = null;
let refreshMSCallPromise: any = null;
let isRefreshingMsToken: boolean = false;

const axiosInstance = axios.create({
  timeout: constants.API_TIMEOUT,
  withCredentials: true
});

const mgr = new UserManager({ ...oidcConfig });

/* istanbul ignore next */
const getAuthHeader = (error: any) => {
  const authHeader = error.config.headers.Authorization as string;
  const newAccessToken = localStorage.getItem("access_token");

  return authHeader?.includes("Bearer")
    ? "Bearer " + newAccessToken
    : newAccessToken;
};

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  /* istanbul ignore next */
  async function (error: any) {
    if (
      error.response?.status === 401 &&
      error.response?.data?.errorCode === 401
    ) {
      if (!isRefreshingMsToken) {
        isRefreshingMsToken = true;
        refreshMSCallPromise = new Promise((resolve, reject) => {
          requestMsToken()
            .then((response) => {
              localStorage.setItem("ms_token", response?.ms_token);
              localStorage.setItem("ms_cookie", response?.ms_cookie);
              resolve(response);
            })
            .catch((e) => {
              isRefreshingMsToken = false;
              refreshMSCallPromise = null;
              reject(
                new Error(
                  getFirstDefined(e?.message, "MS Token refresh failed")
                )
              );
            });
        });
      }
      if (refreshMSCallPromise !== null) {
        await refreshMSCallPromise;
        isRefreshingMsToken = false;
        refreshMSCallPromise = null;
        error.config.headers.ms_token = getMSToken();
        error.config.headers.ms_cookie = getMSCookie();
      }
      return axiosInstance(error.config);
    } else if (
      error?.response?.status === 401 ||
      (typeof error?.response === "object" && error.response.status === 0)
    ) {
      if (getLimit() <= 0) {
        return Promise.reject(
          new Error(
            getFirstDefined(error?.message, "Token refresh limit exceeded")
          )
        );
      }

      if (!isRefreshingToken) {
        isRefreshingToken = true;
        refreshCallPromise = new Promise((resolve, reject) => {
          mgr
            .signinSilent()
            .then((user) => {
              const accessToken = user.access_token;
              const idToken = user.id_token;

              localStorage.setItem("access_token", accessToken);
              localStorage.setItem("id_token", idToken);
              localStorage.setItem(
                `oidc.user:${authority}:${clientId}`,
                JSON.stringify(user)
              );
              resolve(user);
            })
            .catch((e) => {
              mgr.signinRedirect().catch((e) => logError(e));
              isRefreshingToken = false;
              refreshCallPromise = null;
              reject(
                new Error(getFirstDefined(e?.message, "Token refresh failed"))
              );
            });
        });
      }

      if (refreshCallPromise !== null) {
        await refreshCallPromise;
        isRefreshingToken = false;
        refreshCallPromise = null;
      }

      error.config.headers.Authorization = getAuthHeader(error);

      return axiosInstance(error.config);
    }
    return Promise.reject(
      new Error(getFirstDefined(error?.message, "Token refresh failed"))
    );
  }
);

const setUpConfig = (
  isAuthenticated: boolean,
  bearer: boolean,
  signal?: AbortSignal | null
) => {
  const CONFIG = { ...DEFAULT_CONFIG };

  CONFIG.headers["x-request-id"] = uuidv4();
  CONFIG.headers["x-correlation-id"] = uuidv4();
  CONFIG.headers.appRequestID = uuidv4();
  CONFIG.headers.ms_token = getMSToken();
  CONFIG.headers.ms_cookie = getMSCookie();

  if (isAuthenticated) {
    CONFIG.headers.Authorization =
      (bearer ? "Bearer " : "") + localStorage.getItem("access_token");
  }
  if (signal) {
    CONFIG.signal = signal;
  }

  return CONFIG;
};

const setUpConfigMultiPart = (isAuthenticated: boolean, bearer: boolean) => {
  const CONFIG = { ...MULTIPART_CONFIG };
  CONFIG.headers["x-request-id"] = uuidv4();
  CONFIG.headers["x-correlation-id"] = uuidv4();

  if (isAuthenticated) {
    CONFIG.headers.Authorization =
      (bearer ? "Bearer " : "") + localStorage.getItem("access_token");
  }

  return CONFIG;
};

const apiToast = ({ title, message }: { title?: string; message?: string }) =>
  Toast.info({
    title,
    message
  });

/* istanbul ignore next */
const networkErrorHandler = (error: any, showErrorToast: boolean = true) => {
  let title = "API Error";
  let errorMessage = "Something went wrong. Please try again later.";

  // When response status code is out of 2xx range
  if (error?.response?.status === 400) {
    const errorMessage400 = Object.keys(error.response?.data?.errors)
      ?.map((key) => error.response?.data?.errors[key])
      ?.map((messageArr) => messageArr[0])
      ?.join(", ");

    errorMessage = errorMessage400 || errorMessage;
  } else if (error?.response?.status === 401) {
    title = "Authorization Error";
  } else if (error?.code === "ERR_CANCELED") {
    errorMessage = "ERR_CANCELED";
  } else {
    errorMessage =
      error?.response?.data?.error ??
      error?.response?.data?.message ??
      errorMessage;
  }

  if (showErrorToast) {
    apiToast({ title, message: errorMessage });
  }

  throw new Error(errorMessage);
};

const getQueryString = (paramObj: object) => {
  const objKeys = Object.keys(paramObj) as Array<keyof object>;
  const allParams = objKeys.map(
    (key: keyof object) => key + "=" + paramObj[key]
  );

  return allParams.join("&");
};
const getQueryNonEmptyString = (paramObj: object) => {
  let objKeys = Object.keys(paramObj) as Array<keyof object>;
  objKeys = objKeys.filter((key: keyof object) => {
    if (paramObj[key] !== "" && paramObj[key]) {
      return key + "=" + paramObj[key];
    }
  });
  const allParams = objKeys.map(
    (key: keyof object) => key + "=" + paramObj[key]
  );
  return allParams.join("&");
};

const get = async (
  URL: string,
  isAuthenticated: boolean = true,
  bearer: boolean = false,
  showErrorToast: boolean = false,
  signal?: AbortSignal | null
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer, signal);
    const result = await axiosInstance.get(URL, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e, showErrorToast);
  }
};

const getResponseAsBlob = async (
  URL: string,
  isAuthenticated: boolean = true,
  bearer: boolean = false
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer);
    const result = await axiosInstance.get(URL, {
      ...config,
      responseType: "blob"
    });
    return result.data;
  } catch (e) {
    networkErrorHandler(e);
  }
};

const post = async (
  URL: string,
  PAYLOAD: any = {},
  isAuthenticated: boolean = true,
  bearer: boolean = false,
  showErrorToast?: boolean,
  signal?: AbortSignal | null
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer, signal);
    const result = await axiosInstance.post(URL, PAYLOAD, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e, showErrorToast);
  }
};

const uploadDoc = async (
  URL: string,
  PAYLOAD: any = {},
  isAuthenticated: boolean = true,
  bearer: boolean = false
) => {
  try {
    const config = setUpConfigMultiPart(isAuthenticated, bearer);
    const result = await axiosInstance.post(URL, PAYLOAD, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e);
  }
};

const deleteItem = async (
  URL: string,
  isAuthenticated: boolean = true,
  bearer: boolean = false
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer);
    const result = await axiosInstance.delete(URL, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e);
  }
};

const put = async (
  URL: string,
  PAYLOAD: any = {},
  isAuthenticated: boolean = true,
  bearer: boolean = false
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer);
    const result = await axiosInstance.put(URL, PAYLOAD, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e);
  }
};

const patch = async (
  URL: string,
  PAYLOAD: any = {},
  isAuthenticated: boolean = true,
  bearer: boolean = false
) => {
  try {
    const config = setUpConfig(isAuthenticated, bearer);
    const result = await axiosInstance.patch(URL, PAYLOAD, config);
    return result.data;
  } catch (e) {
    networkErrorHandler(e);
  }
};

export {
  post,
  get,
  put,
  patch,
  getQueryString,
  getQueryNonEmptyString,
  getResponseAsBlob,
  deleteItem,
  uploadDoc
};
